import {
  IconBronzeTrophyColor,
  IconSilverTrophyColor,
  IconTrophyColor,
} from '@macarongfactory/web-icons'
import { Label, Text } from '@macarongfactory/web-ui'

import type { ComponentType } from 'react'

/** 트로피 아이콘 크기(px) — Figma 16 */
const TROPHY_ICON_SIZE_PX = 16

/**
 * 순위별 트로피 아이콘 — DS 멀티컬러 아이콘(web-icons 0.3.3).
 * 1위 금(ic_trophy_color) / 2위 은(ic_silver_trophy_color) / 3위 동(ic_bronze_trophy_color).
 * 컬러가 에셋에 내장돼 있어 별도 색 지정 불필요.
 */
const TROPHY_ICON: Record<
  number,
  ComponentType<{ width: number; height: number }>
> = {
  1: IconTrophyColor,
  2: IconSilverTrophyColor,
  3: IconBronzeTrophyColor,
}

/** 뱃지 배경·텍스트 색 (Figma 9591:28606 — 1위 텍스트 orange/700 #F66300) */
const PODIUM_STYLE: Record<number, { bg: string; color: string }> = {
  1: { bg: 'rgba(251, 170, 22, 0.15)', color: '#F66300' },
  2: { bg: '#E3E7EA', color: '#505C66' },
  3: { bg: 'rgba(183, 153, 43, 0.15)', color: '#6A522C' },
}

/**
 * 순위 뱃지 — 1~3위는 트로피 배너(하단 V 노치), 4위 이하는 라벨.
 * (Figma `뱃지` 8624:153047 — 상위 포디움 컬러 배너 / 하위 neutral 라벨)
 */
/**
 * 4위+ 라벨 세로 위치(pt) — Figma 랭킹 리스트 행별 실측 (9233:27738 하위).
 * - desktopStat(데스크탑 만족도·실연비): 16px (Figma 배지 9591:28626, REST paddingTop=16)
 * - desktopFuel(데스크탑 유류비): 16px(x4)
 * - mobileStat(모바일 만족도·실연비): 28px
 * - mobileFuel(모바일 유류비): 14px(x3.5)
 */
type TLabelPosition =
  | 'desktopStat'
  | 'desktopFuel'
  | 'mobileStat'
  | 'mobileFuel'
const LABEL_POSITION_PT: Record<TLabelPosition, string> = {
  desktopStat: 'pt-dimension-x4',
  desktopFuel: 'pt-dimension-x4',
  mobileStat: 'pt-[28px]',
  mobileFuel: 'pt-dimension-x3-5',
}

const RankBadge = ({
  rank,
  labelPosition = 'mobileStat',
}: {
  rank: number
  /** 4위+ 라벨 세로 위치 — 행별(device×type) pt */
  labelPosition?: TLabelPosition
}) => {
  const podium = PODIUM_STYLE[rank]
  const TrophyIcon = TROPHY_ICON[rank]

  if (!podium || !TrophyIcon) {
    // 4위+ 라벨은 콘텐츠 hug(고정폭 미적용) — Figma에선 medal(1~3위)만 w-[31px].
    // 폰트는 medal 배지와 동일한 12px(size='small'=caption-1-semibold). large는 14px라 미사용.
    return (
      <div
        className={`flex flex-col items-center justify-end whitespace-nowrap [&_*]:whitespace-nowrap ${LABEL_POSITION_PT[labelPosition]}`}
      >
        <Label text={`${rank}위`} size='small' />
      </div>
    )
  }

  return (
    // 배지 컨테이너 고정폭 31px (Figma 9591:28606). 안쪽 트로피·순위 텍스트는 hug.
    <div className='flex w-[31px] flex-col items-stretch'>
      <div
        className='flex flex-col items-center px-dimension-x1-5 pt-dimension-x2'
        style={{ backgroundColor: podium.bg }}
      >
        <TrophyIcon width={TROPHY_ICON_SIZE_PX} height={TROPHY_ICON_SIZE_PX} />
        <Text
          variant='caption-1-semibold'
          color={podium.color}
          className='whitespace-nowrap'
        >
          {rank}위
        </Text>
      </div>
      <div
        aria-hidden
        className='h-dimension-x3'
        style={{
          backgroundColor: podium.bg,
          clipPath: 'polygon(0 0, 100% 0, 100% 100%, 50% 55%, 0 100%)',
        }}
      />
    </div>
  )
}

export default RankBadge
